<script>
import BarChart from "@/components/bar-chart.vue";
import HorizontalBarChart from "@/components/horizontal-bar-chart.vue";
import PieChart from "@/components/pie-chart.vue";
import { toCurrency } from "@/helpers/toCurrency";
import GanttChart from "./shared/gantt-chart.vue";
import OverviewFiltersModal, {
  createEmptyFilters
} from "./shared/overview-filters-modal.vue";
import { ChartColorsEnum, CHART_COLORS } from "./constants";
import { uniq } from "lodash";
import { mapGetters } from "vuex";

/**
 * @typedef {Object} GanttItem
 * @property {string} data_inicio - Data de início no formato YYYY-MM-DD
 * @property {string} data_fim - Data de fim no formato YYYY-MM-DD
 * @property {string} business_plans - Nome do item/projeto
 */

/**
 * @typedef {Object} ImpactoAdicional
 * @property {number} ano - Ano base para o cálculo de impacto
 * @property {number} people - Impacto em pessoas
 * @property {number} no_people - Impacto não relacionado a pessoas
 * @property {number} total - Somatório de people e no_people
 */

/**
 * @typedef {Object} OrcamentoCategoria
 * @property {number} people - Percentual do orçamento para pessoas (%)
 * @property {number} no_people - Percentual do orçamento não relacionado a pessoas (%)
 */

/**
 * @typedef {Object} OrcamentoComprometido
 * @property {number} total - Valor total do orçamento
 * @property {number} projetado - Percentual projetado (%)
 * @property {number} realizado - Percentual realizado (%)
 */

/**
 * @typedef {Object} StatusComprometimento
 * @property {string} status - Código do status
 * @property {string} label - Label/descrição do status
 * @property {string} valor - Valor em string formatada
 * @property {number} percentual - Percentual do status (%)
 */

/**
 * @typedef {Object} ReceitaReducaoDespesa
 * @property {string} tipo_impacto_financeiro - Tipo de impacto financeiro ("Receita" ou "Red. Despesas")
 * @property {number} total - Valor total
 * @property {number} "Em andamento" - Valor em andamento
 * @property {number} Aprovação - Valor em aprovação
 * @property {number} "Pré-amortização" - Valor em pré-amortização
 */

/**
 * @typedef {Object} DashboardOrcamento
 * @property {GanttItem[]} gantt - Array de itens do gráfico de Gantt
 * @property {ImpactoAdicional[]} impacto_adicional - Array de impactos adicionais por ano
 * @property {OrcamentoCategoria} orcamento_categoria - Distribuição do orçamento por categoria
 * @property {OrcamentoComprometido} orcamento_comprometido - Informações do orçamento comprometido
 * @property {StatusComprometimento[]} status_comprometimento - Array de status de comprometimento
 * @property {ReceitaReducaoDespesa[]} receita_reducao_despesa - Array de receitas e reduções de despesa
 */

/**
 * @function createEmptyData
 * @returns {DashboardOrcamento}
 */
const createEmptyData = () => ({
  gantt: [],
  impacto_adicional: [],
  orcamento_categoria: {
    people: 0,
    no_people: 0
  },
  orcamento_comprometido: {
    total: 0,
    projetado: 0,
    realizado: 0
  },
  status_comprometimento: [],
  receita_reducao_despesa: []
});

const parseNumber = number =>
  parseFloat(number).toLocaleString("pt-br", {
    maximumFractionDigits: 2
  });

export default {
  components: {
    BarChart,
    GanttChart,
    HorizontalBarChart,
    OverviewFiltersModal,
    PieChart
  },
  data() {
    return {
      beginYear: 0,
      endYear: 0,
      data: createEmptyData(),
      filters: createEmptyFilters()
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    /**
     * @type {import('./shared/gantt-chart.vue').GanttData[]}
     * @readonly
     */
    gantt() {
      return this.data.gantt.map(({ nome, data_inicio, data_fim }) => ({
        label: nome,
        begin: data_inicio,
        finish: data_fim
      }));
    },
    offsetBack() {
      return !this.beginYear || !this.filters.ano_base
        ? 2
        : Math.abs(this.filters.ano_base - this.beginYear);
    },
    offsetForward() {
      return !this.endYear || !this.filters.ano_base
        ? 3
        : Math.abs(this.filters.ano_base - this.endYear);
    },
    orcamentoCategoria() {
      const { no_people, people } = this.data.orcamento_categoria;
      const data = [people, no_people];
      const chartdata = {
        labels: ["People", "Non-People"],
        datasets: [
          {
            backgroundColor: [
              ChartColorsEnum.darkestBlue,
              ChartColorsEnum.darkBlue_25
            ],
            data,
            label: "Pizza"
          }
        ]
      };
      const options = {
        cutoutPercentage: 50,
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "right"
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const percent = parseNumber(
                data.datasets[0].data[tooltipItem.index]
              );
              return `${percent}%`;
            }
          }
        }
      };

      return { chartdata, options };
    },
    orcamentoComprometido() {
      const { projetado, realizado, total } = this.data.orcamento_comprometido;
      const chartdata = {
        labels: [
          `${toCurrency(total * realizado)} - ${parseNumber(realizado)}%`
        ],
        datasets: [
          {
            backgroundColor: [ChartColorsEnum.darkBlue_25, "transparent"],
            borderColor: [ChartColorsEnum.darkBlue_25, "black"],
            borderWidth: 1,
            data:
              realizado >= 100
                ? [realizado]
                : [realizado, projetado - realizado],
            label: "Pizza"
          }
        ]
      };
      const options = {
        cutoutPercentage: 50,
        circumference: Math.PI,
        rotation: Math.PI,
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "bottom"
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) =>
              tooltipItem.index > 0
                ? `Total: ${toCurrency(total)}`
                : `${data.datasets[0].data[0]}%`
          }
        }
      };

      return { chartdata, options };
    },
    impactoAdicional() {
      const data = this.data.impacto_adicional;
      const chartdata = {
        labels: data.map(({ ano }) => ano),
        datasets: [
          {
            label: "People",
            stack: "stacked",
            data: data.map(({ people }) => people),
            backgroundColor: ChartColorsEnum.darkestBlue,
            borderColor: ChartColorsEnum.darkestBlue,
            borderWidth: 1
          },
          {
            label: "Non-People",
            stack: "stacked",
            data: data.map(({ no_people }) => no_people),
            backgroundColor: ChartColorsEnum.darkBlue_25,
            borderColor: ChartColorsEnum.darkBlue_25,
            borderWidth: 1
          }
        ]
      };
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true
            }
          ],
          yAxes: [
            {
              ticks: {
                callback(value) {
                  return toCurrency(value)
                    .replace("R$", "")
                    .trim();
                }
              }
            }
          ]
        },
        legend: {
          position: "bottom"
        },
        tooltips: {
          mode: "index",
          callbacks: {
            label(tooltipItem, data) {
              const ref = data.datasets[tooltipItem.datasetIndex];
              let label = ref.label || "";

              if (label) {
                label += ": ";
              }

              if (tooltipItem?.value !== null) {
                label += new Intl.NumberFormat("pt-BR").format(
                  tooltipItem.value
                );
              }

              return label;
            },
            afterBody(tooltipItem) {
              if (tooltipItem?.length > 1) {
                const total = tooltipItem.reduce(
                  (sum, { value }) => sum + value * 1,
                  0
                );
                return [
                  "Total: " + new Intl.NumberFormat("pt-BR").format(total)
                ];
              }
            }
          }
        }
      };

      return { chartdata, options };
    },
    receitas() {
      const data = this.data.receita_reducao_despesa;
      const keys = uniq(
        data.flatMap(item =>
          Object.keys(item).filter(
            key => !["tipo_impacto_financeiro", "total"].includes(key)
          )
        )
      );
      const chartdata = {
        labels: data.map(
          ({ tipo_impacto_financeiro }) => tipo_impacto_financeiro
        ),
        datasets: keys.map((key, i) => {
          const color = CHART_COLORS[(i * 3) % CHART_COLORS.length];

          return {
            label: key,
            stack: "stacked",
            data: data.map(item => item[key] || 0),
            backgroundColor: color,
            borderColor: color,
            borderWidth: 1,
            barThickness: 32
          };
        })
      };
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              ticks: {
                callback(value) {
                  return new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  }).format(value);
                }
              }
            }
          ],
          yAxes: [
            {
              stacked: true
            }
          ]
        },
        legend: {
          position: "bottom"
        },
        tooltips: {
          mode: "index",
          callbacks: {
            label(tooltipItem, data) {
              const ref = data.datasets[tooltipItem.datasetIndex];
              let label = ref.label || "";

              if (label) {
                label += ": ";
              }

              if (tooltipItem?.value !== null) {
                label += new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL"
                }).format(tooltipItem.value);
              }

              return label;
            },
            afterBody(tooltipItem) {
              if (tooltipItem?.length > 1) {
                const total = tooltipItem.reduce(
                  (sum, { value }) => sum + value * 1,
                  0
                );
                return [
                  "Total: " +
                    new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(total)
                ];
              }
            }
          }
        }
      };

      return { chartdata, options };
    },
    statusComprometimento() {
      const data = this.data.status_comprometimento || [];
      const chartdata = {
        labels: data.map(({ label }) => label),
        datasets: [
          {
            backgroundColor: CHART_COLORS,
            data: data.map(({ valor }) => valor),
            label: "Pizza"
          }
        ]
      };
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "right"
        },
        tooltips: {
          callbacks: {
            label: tooltipItem => {
              const { percentual, valor } = data[tooltipItem.index];
              return `${toCurrency(valor)} - ${percentual}%`;
            }
          }
        }
      };

      return { chartdata, options };
    },
    yearsOpts() {
      const length = Math.abs(this.endYear - this.beginYear) + 1;

      if (length === 1) {
        return [];
      }

      return Array.from({ length }, (_, index) => {
        const value = Math.min(this.beginYear, this.endYear) + index;

        return {
          label: value.toString(),
          value
        };
      });
    }
  },
  created() {
    if (this.clientId === 0) {
      this.goToHome();
    }
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
      } else {
        this.doLoad();
      }
    }
  },
  methods: {
    async doLoad(filters) {
      try {
        if (filters) {
          this.filters.business_plans = filters.business_plans;
        }

        const { ano_base, business_plans } = this.filters;
        let params = [];

        if (ano_base) {
          params.push(`filters[ano_base]=${ano_base}`);
        }

        if (Array.isArray(business_plans) && business_plans.length > 0) {
          params.push(`filters[business_plans]=${business_plans.join(";")}`);
        }

        const query = params.length > 0 ? `?${params.join("&")}` : "";
        const { get } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/dashboard${query}`
        );
        this.data = await get();
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }

        this.data = createEmptyData();
      }
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    }
  }
};
</script>

<template>
  <v-row>
    <v-col cols="12" class="d-flex justify-space-between">
      <div>
        <h1>Gestão de projetos - CAPEX</h1>
        <span>Overview</span>
      </div>

      <div class="d-flex" style="gap: 1rem">
        <v-select
          v-if="yearsOpts.length"
          v-model="filters.ano_base"
          class="ano-base"
          clearable
          dense
          hide-details
          :items="yearsOpts"
          item-text="label"
          item-value="value"
          label="Ano"
          outlined
          :menu-props="{
            offsetY: true,
            bottom: true
          }"
          @change="doLoad()"
        />

        <OverviewFiltersModal
          v-if="clientId"
          v-model="filters"
          @filter="doLoad"
          @beginYear="beginYear = $event"
          @endYear="endYear = $event"
        >
          <template v-slot:activator="{ on }">
            <v-btn class="pa-5" color="#fff" @click.stop="on.click">
              <v-icon>mdi-filter-variant</v-icon>
              Filtros
            </v-btn>
          </template>
        </OverviewFiltersModal>
      </div>
    </v-col>
    <v-col class="d-flex" cols="12" md="6" lg="3">
      <v-card class="px-5">
        <v-card-title>Orçamento por categoria</v-card-title>
        <PieChart v-bind="orcamentoCategoria"></PieChart>
      </v-card>
    </v-col>
    <v-col class="d-flex" cols="12" md="6" lg="3">
      <v-card class="px-5">
        <v-card-title>Orçamento comprometido</v-card-title>
        <PieChart v-bind="orcamentoComprometido"></PieChart>
      </v-card>
    </v-col>
    <v-col class="d-flex" cols="12" md="6" lg="3">
      <v-card class="px-5">
        <v-card-title>Status de comprometimento</v-card-title>
        <PieChart v-bind="statusComprometimento"></PieChart>
      </v-card>
    </v-col>
    <v-col class="d-flex" cols="12" md="6" lg="3">
      <v-card class="px-5">
        <v-card-title>Impacto D&A - Adicional</v-card-title>
        <BarChart v-bind="impactoAdicional"></BarChart>
      </v-card>
    </v-col>
    <v-col class="d-flex" cols="12">
      <v-card class="px-5">
        <HorizontalBarChart
          v-bind="receitas"
          class="horizontal-bar-chart"
        ></HorizontalBarChart>
      </v-card>
    </v-col>
    <v-col class="d-flex" cols="12">
      <v-card>
        <GanttChart
          :data="gantt"
          :offsetBack="offsetBack"
          :offsetForward="offsetForward"
          :year="filters.ano_base"
        />
      </v-card>
    </v-col>
  </v-row>
</template>

<style scoped lang="scss">
.v-card {
  flex-grow: 1;
}

.v-card__title {
  word-break: normal !important;
}

.horizontal-bar-chart {
  max-height: 200px;
}

.ano-base::v-deep .v-input__control {
  background-color: #fff;
  width: 125px;
}
</style>
